/* eslint-disable @typescript-eslint/no-unused-vars */
import { Button } from '@atoms';
import { CloseIcon } from '@atoms/Icons/CloseIcon';
import { UploadIcon } from '@atoms/Icons/UploadIcon';
import { Table } from '@molecules/Table';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import { Box, fontSize } from '@mui/system';
import { errorMessage } from '@services';
import React, { useEffect, useRef, useState } from 'react';
import { LabelUploadCSV } from './styles';
import { DataGrid, GridColumns, GridValueGetterParams } from '@mui/x-data-grid';
import { DATE_FORMAT } from '@constants';
import dayjs from 'dayjs';
import {
  CaseState,
  caseStateMapper,
  postTypeMapper,
  socialMediaCategoryMapper,
} from '@services/types/api';
import CaseStateCell from 'src/features/SocialMediaLinks/components/case-state-cell';
import PlatformInfo from './PlatformInfor';
import ComplianceStatus from './ComplianceStatus';
interface IUploadCSVModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: (data: any) => void;
  loading: boolean;
  uploadSuccess: boolean;
}

interface CSVData {
  platform: string;
  accountUrl: string;
  mainUrl: string;
  accountName: string;
  category: string;
  postType: string;
  caseState: string;
  complianceStatus: string;
  lastEnforcementDate: string;
  index: number;
}

const formatErrorWithIndexes = (errorMessage: string, indexes: number[]) => {
  let start = indexes[0];
  let end = indexes[0];
  const errorLines: string[] = [];
  indexes.forEach((value, index) => {
    if (index !== 0) {
      if (value - 1 === end) {
        end = value;
      } else {
        start === end
          ? errorLines.push(`line ${start + 1} `)
          : errorLines.push(`${start + 1}~${end + 1} lines`);
        start = value;
        end = value;
      }
    }

    if (index === indexes.length - 1) {
      start === end
        ? errorLines.push(` line ${start + 1} `)
        : errorLines.push(`${start + 1}~${end + 1} lines`);
    }
  });

  return `${errorMessage} (${errorLines.join(',')})`;
};

export const UploadCSVModal = ({
  open,
  onCancel,
  onConfirm,
  loading,
  uploadSuccess,
}: IUploadCSVModalProps) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dataTable, setDataTable] = useState<any[]>([]);
  const inputRef = useRef<any>(null);

  const errorMessagesRef = useRef<string[]>([]);

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
  };

  const readFile = async (file: Blob) => {
    const reader = new FileReader();
    reader.readAsText(file);
    return new Promise((resolve, reject) => {
      reader.onloadend = (e: any) => resolve(e.target.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const parseCSV = (text: string): any[] => {
    const lines = text.split('\n');
    const result = lines.map((line) =>
      line.split(',').map((item) => item.trim().replaceAll('\r', '')),
    );
    return result;
  };

  const _validateInvalidFormatData = (validateDataCSV: CSVData[]): CSVData[] => {
    const invalidIndexes: number[] = [];
    const result: CSVData[] = [];
    validateDataCSV.forEach((data) => {
      if (!data.accountName || !data.platform || !data.accountUrl || !data.mainUrl) {
        invalidIndexes.push(data.index);
      } else {
        result.push(data);
      }
    });

    if (!validateDataCSV.length) {
      errorMessagesRef.current.push('There is an error in the input format.');
    }

    if (invalidIndexes.length) {
      errorMessagesRef.current.push(
        formatErrorWithIndexes('Invalid input format.', invalidIndexes),
      );
    }

    return result;
  };

  const _formatCsvData = (csvData: any): any[] => {
    const data = csvData.slice(1);
    try {
      const result: any[] = data.map((item: any, index: number) => ({
        platform: item[0] || null,
        accountUrl: item[3] || null,
        mainUrl: item[4] || null,
        accountName: item[1] || null,
        category: item[5] || null,
        caseState: item[8] || null,
        postType: item[6] || null,
        complianceStatus: item[7] || null,
        lastEnforcementDate: item[9] || null,
        index,
      }));
      return result;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const _validateData = (data: CSVData[]): CSVData[] => {
    let validatedData = [...data];
    validatedData = _validateInvalidFormatData(validatedData);

    return validatedData;
  };

  const _validateHeadersCSV = (csvData: any): boolean => {
    const headers = csvData[0];

    const requiredHeaders = [
      'Platform',
      'Account Name',
      'Thumbnail',
      'Account Url',
      'Main Url',
      'Category',
      'Compliance Status',
      'Case State',
      'Last Enforcement Date',
    ];
    const missingHeaders = requiredHeaders.filter((header) => !headers.includes(header));
    if (missingHeaders.length) {
      errorMessagesRef.current.push(`Missing required headers: ${missingHeaders.join(', ')}`);
      return false;
    }
    return true;
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);

    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const fileList = event.dataTransfer.files;
      const eventDataTemp = {
        target: {
          files: fileList,
          value: '',
        },
      };
      await handleFileChange(eventDataTemp);
    }
  };

  const handleFileChange = async (e: any) => {
    if (!e.target.files.length) {
      return;
    }

    const inputFile = e.target.files[0];
    const fileExtension = inputFile?.type.split('/')[1];

    if (!['csv'].includes(fileExtension)) {
      errorMessage('Please upload a valid CSV file. Supported formats: .csv');
      e.target.value = '';
      return;
    }
    try {
      /* Read file */
      const fileData = await readFile(inputFile);
      const parsedData = parseCSV(fileData as string);
      const validateHeaders = _validateHeadersCSV(parsedData);
      if (!validateHeaders) {
        e.target.value = '';
        errorMessage(errorMessagesRef.current.join(' '));
        errorMessagesRef.current = [];
        return;
      }
      const formattedData = _formatCsvData(parsedData);
      const validatedData = _validateData(formattedData);
      if (errorMessagesRef.current.length > 0) {
        e.target.value = '';
        errorMessage(errorMessagesRef.current.join(' '));
        errorMessagesRef.current = [];
        return;
      }
      setSelectedFile(inputFile);
      setDataTable(
        validatedData.map((item, index) => ({
          ...item,
          id: index + 1,
        })),
      );
    } catch (error: any) {
      errorMessage('Error reading file. Please try again.');
      console.error(error);
    }

    e.target.value = '';
  };

  const removedCellClassName = (params: any) => {
    return params.row.removed ? 'removed-row' : '';
  };

  const multiLineEllipsisStyle = {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '9px',
  };

  const columns: GridColumns<{ category: string | number | symbol; id: string }> = [
    {
      field: 'id',
      headerName: '',
      minWidth: 18,
      maxWidth: 40,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.platform,
      renderCell: ({ row: { id } }: any) => <Typography sx={{ fontSize: 9 }}>{id}</Typography>,
      sortable: false,
      filterable: true,
    },
    {
      field: 'platform',
      headerName: 'Platform',
      minWidth: 100,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.platform,
      renderCell: ({ row: { platform } }: any) => <PlatformInfo platform={platform} />,
      sortable: false,
      filterable: true,
    },
    {
      field: 'accountUrl',
      headerName: 'Page/Account URL',
      minWidth: 100,
      sortable: false,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.accountUrl,
      renderCell: ({ row: { accountUrl } }: any) => (
        <Link
          className="ellipsis"
          sx={{ ...multiLineEllipsisStyle }}
          href={accountUrl}
          target="_blank"
        >
          {accountUrl}
        </Link>
      ),
      filterable: false,
    },
    {
      field: 'mainUrl',
      headerName: 'Main URL',
      sortable: false,
      minWidth: 110,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.mainUrl,
      renderCell: ({ row: { mainUrl } }: any) => (
        <Link
          className="ellipsis"
          href={mainUrl}
          target="_blank"
          sx={{ ...multiLineEllipsisStyle }}
        >
          {mainUrl}
        </Link>
      ),
      filterable: false,
    },
    {
      field: 'accountName',
      headerName: 'Account Name',
      minWidth: 90,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.accountName,
      renderCell: ({ row: { accountName } }: any) => (
        <Box sx={{ ...multiLineEllipsisStyle }}>{accountName}</Box>
      ),
      sortable: false,
      filterable: false,
    },
    {
      field: 'category',
      headerName: 'Category',
      minWidth: 90,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.category,
      renderCell: ({ row: { category } }: any) => (
        <Box sx={{ ...multiLineEllipsisStyle }}>
          {socialMediaCategoryMapper[category as keyof typeof socialMediaCategoryMapper]}
        </Box>
      ),
      filterable: false,
    },
    {
      field: 'postType',
      headerName: 'Case Type',
      minWidth: 70,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.postType,
      renderCell: ({ row: { postType } }: any) => (
        <Box sx={{ ...multiLineEllipsisStyle }}>
          {postTypeMapper[postType as keyof typeof postTypeMapper]}
        </Box>
      ),
      filterable: false,
    },
    {
      field: 'caseState',
      headerName: 'Case Status',
      minWidth: 70,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.caseState,
      renderCell: ({ row: { caseState, id } }: any) => (
        <>
          <Typography
            fontSize="small"
            color={caseState === CaseState.Closed ? '#D4D5D6' : undefined}
            sx={{ ...multiLineEllipsisStyle }}
          >
            {caseStateMapper[caseState as keyof typeof caseStateMapper]}
          </Typography>
        </>
      ),
      filterable: false,
    },
    {
      field: 'complianceStatus',
      headerName: 'Compliance Status',
      minWidth: 100,
      flex: 1,
      cellClassName: removedCellClassName,
      valueGetter: (params: GridValueGetterParams) => params.row.complianceStatus,
      renderCell: ({ row: { complianceStatus } }: any) =>
        complianceStatus && <ComplianceStatus status={complianceStatus} />,
      filterable: false,
    },
    {
      field: 'enforcementDate',
      headerName: 'Enforcement Date',
      minWidth: 100,
      flex: 1,
      valueGetter: (params: GridValueGetterParams) => params.row.lastEnforcementDate,
      renderCell: ({ row: { lastEnforcementDate } }: any) =>
        lastEnforcementDate && (
          <Box display="flex" alignItems="center" gap="40px">
            <p>
              {dayjs(
                lastEnforcementDate
                  .split('|')
                  .map((item: string) => item.trim())
                  .join('-'),
              ).format(DATE_FORMAT)}
            </p>
          </Box>
        ),
      filterable: false,
    },
  ];

  const onClickUpload = () => {
    inputRef.current?.click();
  };

  const renderTable = () => {
    return (
      <>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(255,255,255,0.7)',
              zIndex: 1301,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={40} />
            <Typography sx={{ ml: 2, fontSize: 14, color: '#464F60' }}>Uploading...</Typography>
          </Box>
        )}
        <Box
          display="flex"
          justifyContent="space-between"
          paddingX="32px"
          alignItems="center"
          sx={{ mt: '24px' }}
        >
          <Typography
            sx={{
              fontSize: '24px',
              lineHeight: '20px',
              color: '#464F60',
              whiteSpace: 'normal',
              marginBottom: '18px',
            }}
          >
            Add New Results
          </Typography>
          <Button
            onClick={onClickUpload}
            sx={{ paddingX: '30px' }}
            variant="outlined"
            color="black"
          >
            Replace CSV
          </Button>
        </Box>
        <Box sx={{ height: '290px', width: '100%', padding: '0 32px' }}>
          <DataGrid
            rows={dataTable}
            columns={columns}
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            disableDensitySelector
            disableVirtualization={false}
            hideFooter={true}
            autoHeight={false}
            checkboxSelection={false}
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                fontSize: '7.52px',
                fontWeight: 'bold',
                border: 'none',
              },
              '& .MuiDataGrid-cell': {
                fontSize: '7.52px',
                border: 'none',
              },
              '& .MuiDataGrid-virtualScroller': {
                overflowX: 'auto',
                overflowY: 'auto',
              },

              '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader': {
                outline: 'none !important',
              },

              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'transparent',
              },

              height: '100%',
              width: '100%',
              border: 'none',
            }}
          />
        </Box>
      </>
    );
  };

  const renderUploadCSV = () => {
    return (
      <Box py="24px" px="32px" height="100%" width="100%">
        <Box display="flex" justifyContent="space-between">
          <Typography variant="h5"></Typography>
          <IconButton
            onClick={onCancel}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: '24px',
              lineHeight: '20px',
              color: '#464F60',
              whiteSpace: 'normal',
              marginBottom: '18px',
            }}
          >
            Upload CSV
          </Typography>
        </Box>
        <Divider />
        <Box display={'flex'} alignItems="center" marginTop="10px">
          <Typography
            sx={{
              fontSize: '14px',
              lineHeight: '20px',
              color: '#000000',
              whiteSpace: 'normal',
            }}
          >
            DATA
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              lineHeight: '20px',
              color: '#A1A9B8',
              whiteSpace: 'normal',
            }}
          >
            (.csv)
          </Typography>
        </Box>

        <LabelUploadCSV htmlFor="file-upload" style={{ cursor: 'pointer' }}>
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              border: '2px dashed #ccc',
              borderRadius: '10px',
              padding: '20px',
              textAlign: 'center',
              cursor: 'pointer',
              width: '852px',
              height: '206px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: dragActive ? '#f0f0f0' : '#F2F4F7',
              margin: '22px 0',
            }}
          >
            <Box
              sx={{
                width: '151px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <UploadIcon />
              <Typography
                sx={{
                  fontSize: '10px',
                  lineHeight: '20px',
                  color: '#A1A9B8',
                  whiteSpace: 'normal',
                }}
              >
                Select a CSV file to upload or drag and drop it here
              </Typography>
            </Box>
          </Box>
        </LabelUploadCSV>
        <Divider />
      </Box>
    );
  };

  const handleCancel = () => {
    setDataTable([]);
    setSelectedFile(null);
    onCancel();
  };

  const handleUpload = () => {
    if (!selectedFile) {
      errorMessage('Please select a file to upload.');
      return;
    }
    onConfirm(selectedFile);
  };

  useEffect(() => {
    if (uploadSuccess) {
      setDataTable([]);
      setSelectedFile(null);
    }
  }, [uploadSuccess]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      PaperProps={{
        sx: {
          borderRadius: '10px',
          minWidth: '913px',
          height: '458px',
          position: 'relative',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          id="file-upload"
          multiple={false}
          ref={inputRef}
        />
        {dataTable.length ? renderTable() : renderUploadCSV()}
      </DialogContent>

      <DialogActions>
        <Button
          variant="text"
          color="inherit"
          onClick={handleCancel}
          sx={{
            width: 'fit-content',
            paddingX: '10px',
            borderRadius: '10px',
            marginTop: '8px',
            marginBottom: '12px',
          }}
        >
          <Box component="div" display="flex" gap={1.25}>
            <Typography component="span" color="text.white" variant="body2">
              Cancel
            </Typography>
          </Box>
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleUpload()}
          sx={{
            width: 'fit-content',
            paddingX: '30px',
            paddingY: '10px',
            borderRadius: '10px',
            marginTop: '8px',
            marginBottom: '12px',
          }}
          disabled={!selectedFile}
        >
          <Typography component="span" color="text.white" variant="body2">
            Import
          </Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};
